import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { deleteInvoice, requeueInvoiceParse } from '../../../api/invoicing';
import { HOST_API_URL } from '../../../config-global';
import { getInvoices } from '../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../redux/store';
import { ConfirmDialog } from '../../../reusable-components/confirm-dialog';
import { createColumns } from '../../../reusable-components/datagrid/custom-filters';
import CustomDataGrid from '../../../reusable-components/datagrid/CustomDataGrid';
import { NEW_INVOICES, PENDING_INVOICES } from '../../../reusable-components/datagrid/invoiceColumns';
import ManagerChipsWrapper from '../../../reusable-components/datagrid/ManagerChipsWrapper';
import PdfPopupButton from '../../../reusable-components/pdf-popup/PdfPopupButton';
import { useSnackbar } from '../../../reusable-components/snackbar';
import { fMonthDayYear } from '../../../utils/formatTime';
import Notes from './Notes';
import { InvoiceApprovalStatus } from '../../../utils/globalEnums';
import ManualInvoiceUploader from './ManualInvoices/ManualInvoiceUploader';
import { ParsingStatusLabel } from './InvoicingParsingStatus/ParsingStatusLabel';
import { isActiveParsingStatus, getParsingStatusLabel } from './InvoicingParsingStatus/ParsingStatusUtils';
import { markMetricRemoved, NotificationMetricType } from '../../../redux/slices/notifications';
import { NotificationIdsChip, useNotificationIdsFilter } from '../../../hooks/useNotificationIdsFilter';
import { useSubmittingIds  } from '../../../context/invoices/InvoiceLiveStatusContext';

InvoicingView.propTypes = {
  CustomToolbar: PropTypes.func,
  setActiveRows: PropTypes.func,
  showSelection: PropTypes.string,
  setSelectedToProcess: PropTypes.func,
  setShowSelection: PropTypes.func,
  localIsLoading: PropTypes.bool,
  openInvoiceUploader: PropTypes.bool,
  setOpenInvoiceUploader: PropTypes.func,
};
export default function InvoicingView({
  CustomToolbar,
  setActiveRows,
  showSelection,
  setShowSelection,
  setSelectedToProcess,
  localIsLoading,
  openInvoiceUploader,
  setOpenInvoiceUploader,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const section = location.pathname.split('/').pop();

  const [searchParams] = useSearchParams();
  const { matchesNotificationIds, isNotificationLoading } = useNotificationIdsFilter([NotificationMetricType.InvoicesNeedApproval, NotificationMetricType.InvoicesSubmitted]);
  const { enqueueSnackbar } = useSnackbar();
  const submittingIds = useSubmittingIds();

  const [data, setData] = useState([]);
  const {
    data: {
      invoicesInParsingProgress,
      manualInvoicing,
      pendingInvoicing,
      pendingFilterInvoicing,
      invoicesAwaitingSubmission,
      invoicesInQueue,
      rejectedInvoices,
      bookingTypeSubmitSchedules,
    },
  } = useSelector((state) => state.invoicing);

  const storedSelection = localStorage.getItem('selectedInvoices');
  const invoicesSelection =
    storedSelection && storedSelection !== undefined && storedSelection !== 'undefined'
      ? JSON.parse(storedSelection) || []
      : [];

  const getInvoiceStatusText = (invoice) => {
    const isEmpty = invoice.status === null || invoice.status === undefined || invoice.status === '';
    const isManual = invoice.sourceMethod === 'Manual';
    const invoiceRejectors =
      isManual && invoice.approvers?.length > 0
        ? invoice.approvers.filter((x) => x.status === InvoiceApprovalStatus.Rejected)
        : [];
    const invoiceRejectorsNames = invoiceRejectors.map((a) => a.approverFullName).join(', ');

    return isManual && invoice.approvalStatus === InvoiceApprovalStatus.Pending
      ? 'Pending Approval'
      : isManual && invoiceRejectors.length > 0
      ? `Rejected by: ${invoiceRejectorsNames}`
      : !isEmpty
      ? invoice.status
      : invoice.queInfo !== null
      ? 'Processed in que'
      : invoice.editInProgress
      ? 'Pending Review'
      : 'Ready to Submit';
  };

  useEffect(() => {
    let data;
    if (section === 'new') {
      data = manualInvoicing;
    } else if (section === 'pending') {
      data = pendingInvoicing;
    } else if (section === 'pendingFilter') {
      data = pendingFilterInvoicing;
    } else if (section === 'awaitingSubmission') {
      data = invoicesAwaitingSubmission;
    } else if (section === 'invoicesInQueue') {
      data = invoicesInQueue;
    } else if (section === 'rejectedInvoices') {
      data = rejectedInvoices;
    }
    const dataWithStatusText = data.filter((invoice) => matchesNotificationIds(invoice.id)).map((invoice) => {
      const statusText = getInvoiceStatusText(invoice);
      const shortenedStatusText = statusText.startsWith('Booking is closed')
        ? 'Booking is closed'
        : statusText.startsWith('Price not within acceptable limits')
        ? 'Price not within acceptable limits'
        : /Invoice '.*' already in Intacct*/i.test(statusText)
        ? 'Invoice is already in Intacct'
        : /GL code '.*' is invalid/i.test(statusText)
        ? 'GL code is invalid'
        : statusText;

      const schedule = bookingTypeSubmitSchedules?.find((s) => s.bookingTypeId === invoice.bookingTypeId);

      return {
        ...invoice,
        statusText: shortenedStatusText,
        fullStatusText: getInvoiceStatusText(invoice),
        tooltipText:
          statusText === 'Ready to Submit' && schedule
            ? `Invoice submission will be attempted ${schedule.nextBookingSchedule}`
            : '',
      };
    });
    setData(dataWithStatusText);
  }, [
    manualInvoicing,
    pendingInvoicing,
    pendingFilterInvoicing,
    invoicesAwaitingSubmission,
    invoicesInQueue,
    rejectedInvoices,
    section,
    setShowSelection,
    matchesNotificationIds,
  ]);

  useEffect(() => {
    const anyStaleParsingInvoices = data.some(
      (x) => !invoicesInParsingProgress?.includes(x.id) && isActiveParsingStatus(x.processingStatus, !!x.editInProgress)
    );

    if (anyStaleParsingInvoices) {
      dispatch(getInvoices(true));
    }
  }, [dispatch, invoicesInParsingProgress, data]);

  const handleDelete = async (id) => {
    const response = await deleteInvoice(id);
    if (response.data.success) {
      enqueueSnackbar('Invoice deleted successfully', { variant: 'success' });
      dispatch(markMetricRemoved(NotificationMetricType.InvoicesSubmitted, id));
      dispatch(markMetricRemoved(NotificationMetricType.InvoicesNeedApproval, id));
      await dispatch(getInvoices(section === 'new'));
    } else {
      enqueueSnackbar(response.data.message, { variant: 'error' });
    }
  };

  const parsingStatus = {
    field: 'processingStatus',
    headerName: 'BobAI Status',
    type: 'customSelect',
    width: 110,
    renderCell: (params) => (
      <ParsingStatusLabel
        processingStatus={params.row.processingStatus}
        disabled={params.row.editingLocked}
        invoiceId={params.row.id}
        onRetry={async (id) => {
          const res = await requeueInvoiceParse(id);
          dispatch(getInvoices(true, [id]));

          if (res.data.success) {
            enqueueSnackbar(res.data.message, { variant: 'success' });
          } else {
            enqueueSnackbar(res.data.message, { variant: 'error' });
          }
        }}
      />
    ),
    id: 'parsingStatus',
    className: 'parsingStatus',
    valueGetter: (params) => getParsingStatusLabel(params.row.processingStatus),
    getFilterValues: (row) => [getParsingStatusLabel(row.processingStatus)],
  };

  const vendorInvoiceId = {
    field: 'vendorInvoiceId',
    headerName: 'Invoice #',
    minWidth: 90,
    flex: 0.8,
    renderCell: (params) => {
      const isQueued = params.row.queInfo !== null;

      return isQueued ? (
        <>
          <Tooltip
            title={
              <>
                <div>Invoice in Que:</div>
                <div>Added by: {params.row.queInfo?.addedBy || ''}</div>
                <div>Added on: {fMonthDayYear(params.row.queInfo.dateAdded)}</div>
                <div>Que processes: {params.row.queInfo.queRunTime}</div>
                <div>Sends to: {params.row.queInfo.sendTo}</div>
              </>
            }
          >
            <Box
              component="div"
              sx={{
                wordWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                overflow: 'auto',
                fontSize: '0.75rem',
                color: 'info.main',
              }}
            >
              {params.value}
            </Box>
          </Tooltip>
        </>
      ) : (
        <Box
          component="div"
          sx={{
            wordWrap: 'break-word',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          {params.value}
        </Box>
      );
    },
    type: 'customSelect',
    valueGetter: (params) => params.row.vendorInvoiceId,
  };
  const notes = {
    field: 'hasChat',
    headerName: 'Chat',
    width: 90,
    renderCell: (params) => (params.row.hasChat ? <Notes rowId={params.row.id} /> : null),
  };

  const ActionButtons = (params) => {
    const { id, editingLocked } = params.row;
    return (
      <>
        {params.row.hasMainFile ? (
          <PdfPopupButton
            icon={params.row.hasMainFile ? <PictureAsPdfOutlinedIcon /> : <PictureAsPdfOutlinedIcon color="disabled" />}
            url={
              params.row.hasMainFile
                ? `${HOST_API_URL}Api/Invoices/ViewInvoiceFile?id=${params.row.id}&mainFile=true`
                : null
            }
            tooltipText={
              params.row.hasMainFile ? (
                <iframe
                  style={{ width: '100%', height: '100%' }}
                  title="Invoice PDF"
                  src={`${HOST_API_URL}Api/Invoices/ViewInvoiceFile?id=${params.row.id}&mainFile=true#toolbar=0`}
                />
              ) : (
                'No Invoice PDF available'
              )
            }
            largeTooltip={Boolean(params.row.hasMainFile)}
            row={params.row}
          />
        ) : (
          <Tooltip title="No Invoice PDF available">
            <div>
              <div
                style={{
                  width: '12px',
                  marginRight: '4px',
                  textAlign: 'right',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                }}
              >
                {' '}
              </div>
              <PictureAsPdfOutlinedIcon color="disabled" sx={{ mr: '8px' }} />
            </div>
          </Tooltip>
        )}

        <Tooltip
          title={
            editingLocked
              ? 'Insufficient permissions for this facility'
              : submittingIds.has(Number(id))
              ? 'Invoice is currently being submitted'
              : ''
          }
          arrow
        >
          <span>
            <ConfirmDialog
              disabled={editingLocked || submittingIds.has(Number(id))}
              content={`Are you sure you want to delete this invoice?`}
              action={() => handleDelete(id)}
            />
          </span>
        </Tooltip>
      </>
    );
  };

  const hasPDFComparator = (v1, v2) => v2.hasMainFile - v1.hasMainFile;

  const actions = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    width: 110,
    renderCell: (params) => ActionButtons(params),
    valueGetter: (params) => params.row,
    id: 'actions',
    className: 'actions',
    sortComparator: hasPDFComparator,
  };
  const downloadEmail = async (id) => {
    window.open(`${HOST_API_URL}Api/Invoices/DownloadManualInvoiceEmail?id=${id}`, '_blank');
  };

  const sentBy = {
    field: 'sentBy',
    headerName: 'Sent by',
    type: 'customSelect',
    renderCell: (params) => {
      const manualInvoiceEmail = params.row.manualInvoiceEmail ?? false;
      return (
        <Tooltip title={params.row.sentBy}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Typography
              variant="subtitle2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flexGrow: 1,
                color: manualInvoiceEmail ? 'secondary.main' : 'text.secondary',
                textDecoration: manualInvoiceEmail ? 'underline' : 'none',
                cursor: 'pointer',
              }}
              onClick={() => downloadEmail(params.row.id)}
            >
              {params.row.sentBy}
            </Typography>
          </Box>
        </Tooltip>
      );
    },
    valueGetter: (params) => params.row.sentBy,
    width: 100,
  };
  const manager = ManagerChipsWrapper(() => dispatch(getInvoices(false)), 'Invoices');
  const conditionalColumns = useMemo(() => {
    if (section === 'new') {
      return createColumns([
        ...NEW_INVOICES.slice(0, 11),
        sentBy,
        vendorInvoiceId,
        manager,
        ...NEW_INVOICES.slice(11),
        notes,
        parsingStatus,
      ]);
    }
    return createColumns([
      ...PENDING_INVOICES.slice(0, 13),
      vendorInvoiceId,
      ...PENDING_INVOICES.slice(13),
      manager,
      notes,
    ]);
  }, [searchParams, section, data]);

  return (
    <>
      <Grid item xs={12}>
        <NotificationIdsChip label="invoices" />
        <CustomDataGrid
          gridId={`invoicing-invoice-view-${section}`}
          key={section}
          applyQuickFilter
          boxSX={{ height: 'calc(100vh - 280px)' }}
          data={data}
          gridColumns={[...conditionalColumns, actions]}
          defaultHiddenColumns={[
            'category',
            'address',
            'shipping',
            'quantity',
            'total',
            'dateAdded',
            'addedBy',
            'processingStatus',
          ]}
          CustomLeftToolbar={CustomToolbar}
          setActiveRows={setActiveRows}
          forceReRender={[submittingIds.size, ...submittingIds]}
          checkboxSelection={showSelection === 'Select invoices' || showSelection === 'Submit invoices'}
          isRowSelectable={(params) =>
            (showSelection === 'Submit invoices' &&
              !params.row.submittingLocked &&
              !submittingIds.has(params.row.id) &&
              params.row.queInfo === null &&
              params.row.approvalStatus === InvoiceApprovalStatus.Approved) ||
            showSelection !== 'Submit invoices'
          }
          onRowSelectionModelChange={(newSelection) => {
            setSelectedToProcess(newSelection);
            if (newSelection.length !== 0) localStorage.setItem('selectedInvoices', JSON.stringify(newSelection || []));
            else localStorage.removeItem('selectedInvoices');
          }}
          rowSelectionModel={invoicesSelection}
          onCellClick={(row, event, api) => {
            const selection = window.getSelection().toString();
            const apiObj = api.api;

            if (selection.length > 0) return;
            if (showSelection === 'Submit invoices' || showSelection === 'Select invoices') {
              const isSelected = apiObj.isRowSelected(row.id);

              if (isSelected) {
                apiObj.selectRow(row.id, false);
              } else {
                apiObj.selectRow(row.id, true);
              }

              return;
            }
            if (
              !['manager', 'actions', 'sentBy', 'hasChat', '__check__'].includes(row.field) &&
              !(section === 'previous' && row.field === 'status')
            ) {
              const invoiceId = row.id;
              navigate(`/dashboard/invoicing/${section}/${invoiceId}`);
            }
          }}
          isLoading={localIsLoading || isNotificationLoading}
          advancedSearchBase={`${section}Invoicing`}
          advancedFilter
        />
      </Grid>
      <ManualInvoiceUploader
        openInvoiceUploader={openInvoiceUploader}
        setOpenInvoiceUploader={setOpenInvoiceUploader}
      />
    </>
  );
}
